import { useState, useMemo, useRef, useEffect } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { useFlipStorage } from "../../../contexts/FlipStorageContext";
import { platformFees } from "../../../mocks/dashboardMocks";

const OTHER_PLATFORM = "Other";
const ALL_PLATFORMS = [...platformFees.map((p) => p.platform), OTHER_PLATFORM];

interface CustomSelectProps {
  value: string;
  options: string[];
  onChange: (val: string) => void;
  isDark: boolean;
}

function CustomSelect({ value, options, onChange, isDark }: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full border rounded-xl px-4 py-3 font-inter text-sm text-left flex items-center justify-between transition-colors focus:outline-none cursor-pointer whitespace-nowrap ${
          isDark
            ? "bg-white/5 border-white/10 text-white hover:border-white/20"
            : "bg-white border-black/10 text-[#111] hover:border-black/20"
        }`}
      >
        <span>{value}</span>
        <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${open ? "rotate-180" : ""} ${isDark ? "text-[#555]" : "text-[#aaa]"}`} />
      </button>
      {open && (
        <div
          className={`absolute z-50 mt-1.5 w-full rounded-xl border overflow-hidden ${
            isDark ? "bg-[#141414] border-white/10" : "bg-white border-black/10"
          }`}
        >
          <div className="max-h-64 overflow-y-auto py-1">
            {options.map((opt) => {
              const isSelected = opt === value;
              const isOtherOpt = opt === OTHER_PLATFORM;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => { onChange(opt); setOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 font-inter text-sm flex items-center justify-between transition-colors cursor-pointer whitespace-nowrap ${
                    isSelected
                      ? isDark ? "bg-white/10 text-white" : "bg-black/8 text-[#111]"
                      : isDark ? "text-[#aaa] hover:bg-white/5 hover:text-white" : "text-[#444] hover:bg-black/4 hover:text-[#111]"
                  } ${isOtherOpt ? `border-t mt-1 pt-3 ${isDark ? "border-white/8" : "border-black/6"}` : ""}`}
                >
                  <span>{opt}</span>
                  {isSelected && <i className={`ri-check-line text-sm ${isDark ? "text-white/60" : "text-[#888]"}`} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProfitCalculator() {
  const { isDark } = useTheme();
  const { addEntry } = useFlipStorage();

  const [itemName, setItemName] = useState("");
  const [buyPrice, setBuyPrice] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [shippingCost, setShippingCost] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState(platformFees[0].platform);
  const [customFee, setCustomFee] = useState("");
  const [calculated, setCalculated] = useState(false);
  const [saved, setSaved] = useState(false);

  const isOther = selectedPlatform === OTHER_PLATFORM;

  const platform = isOther
    ? { platform: "Custom", saleFee: parseFloat(customFee) || 0, paymentFee: 0, shipping: 0 }
    : (platformFees.find((p) => p.platform === selectedPlatform) ?? platformFees[0]);

  const result = useMemo(() => {
    const buy = parseFloat(buyPrice) || 0;
    const sell = parseFloat(sellPrice) || 0;
    const ship = parseFloat(shippingCost) || 0;
    if (sell === 0) return null;
    const saleFeeAmt = (sell * platform.saleFee) / 100;
    const paymentFeeAmt = (sell * platform.paymentFee) / 100;
    const platformShipping = platform.shipping;
    const totalFees = saleFeeAmt + paymentFeeAmt + platformShipping;
    const netProfit = sell - buy - ship - totalFees;
    const roi = buy > 0 ? ((netProfit / (buy + ship)) * 100) : 0;
    const margin = sell > 0 ? ((netProfit / sell) * 100) : 0;
    return { saleFeeAmt, paymentFeeAmt, platformShipping, totalFees, netProfit, roi, margin };
  }, [buyPrice, sellPrice, shippingCost, platform, calculated]);

  const inputClass = `w-full border rounded-xl px-4 py-3 font-inter text-sm focus:outline-none transition-colors ${
    isDark
      ? "bg-white/5 border-white/10 text-white placeholder:text-[#444] focus:border-white/25"
      : "bg-white border-black/10 text-[#111] placeholder:text-[#aaa] focus:border-black/25"
  }`;

  const canCalculate = buyPrice && sellPrice && (!isOther || customFee);

  function handleSave() {
    if (!result || !calculated) return;
    const buy = parseFloat(buyPrice) || 0;
    const sell = parseFloat(sellPrice) || 0;
    addEntry({
      item: itemName.trim() || "Untitled Flip",
      buyPrice: buy,
      sellPrice: sell,
      fees: parseFloat(result.totalFees.toFixed(2)),
      profit: parseFloat(result.netProfit.toFixed(2)),
      platform: isOther ? "Other" : selectedPlatform,
      status: "sold",
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  function handleReset() {
    setItemName("");
    setBuyPrice("");
    setSellPrice("");
    setShippingCost("");
    setSelectedPlatform(platformFees[0].platform);
    setCustomFee("");
    setCalculated(false);
    setSaved(false);
  }

  return (
    <div className={`rounded-2xl p-6 border ${isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6"}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${isDark ? "bg-white/6" : "bg-black/6"}`}>
          <i className={`ri-calculator-line text-base ${isDark ? "text-white/50" : "text-[#666]"}`} />
        </div>
        <div>
          <h3 className={`font-syne font-semibold text-base ${isDark ? "text-white" : "text-[#111]"}`}>
            Profit Calculator
          </h3>
          <p className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>
            Calculate net profit after all fees
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div className="flex flex-col gap-3">
          {/* Item Name */}
          <div>
            <label className={`font-inter text-xs mb-1.5 block ${isDark ? "text-[#555]" : "text-[#999]"}`}>
              Item Name <span className={isDark ? "text-[#3a3a3a]" : "text-[#ccc]"}>(optional)</span>
            </label>
            <input
              type="text"
              placeholder="e.g. Jordan 1 Bred Toe"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              className={inputClass}
            />
          </div>

          {/* Platform selector */}
          <div>
            <label className={`font-inter text-xs mb-1.5 block ${isDark ? "text-[#555]" : "text-[#999]"}`}>
              Platform
            </label>
            <CustomSelect
              value={selectedPlatform}
              options={ALL_PLATFORMS}
              isDark={isDark}
              onChange={(val) => {
                setSelectedPlatform(val);
                setCustomFee("");
                setCalculated(false);
              }}
            />
          </div>

          {/* Custom fee input */}
          {isOther && (
            <div>
              <label className={`font-inter text-xs mb-1.5 flex items-center gap-1.5 ${isDark ? "text-[#555]" : "text-[#999]"}`}>
                <i className="ri-percent-line text-xs" />
                Custom Platform Fee (%)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="e.g. 7.5"
                  value={customFee}
                  onChange={(e) => { setCustomFee(e.target.value); setCalculated(false); }}
                  className={`${inputClass} pr-10`}
                />
                <span className={`absolute right-4 top-1/2 -translate-y-1/2 font-inter text-sm ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>%</span>
              </div>
            </div>
          )}

          <div>
            <label className={`font-inter text-xs mb-1.5 block ${isDark ? "text-[#555]" : "text-[#999]"}`}>
              Buy / Cost Price ($)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 150.00"
              value={buyPrice}
              onChange={(e) => { setBuyPrice(e.target.value); setCalculated(false); }}
              className={inputClass}
            />
          </div>

          <div>
            <label className={`font-inter text-xs mb-1.5 block ${isDark ? "text-[#555]" : "text-[#999]"}`}>
              Sell Price ($)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 320.00"
              value={sellPrice}
              onChange={(e) => { setSellPrice(e.target.value); setCalculated(false); }}
              className={inputClass}
            />
          </div>

          <div>
            <label className={`font-inter text-xs mb-1.5 block ${isDark ? "text-[#555]" : "text-[#999]"}`}>
              Your Shipping Cost ($) <span className={`${isDark ? "text-[#3a3a3a]" : "text-[#ccc]"}`}>(optional)</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 8.50"
              value={shippingCost}
              onChange={(e) => { setShippingCost(e.target.value); setCalculated(false); }}
              className={inputClass}
            />
          </div>

          <button
            type="button"
            onClick={() => setCalculated(true)}
            disabled={!canCalculate}
            className={`w-full font-inter text-sm font-medium py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed mt-1 ${
              isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"
            }`}
          >
            Calculate Profit
          </button>
        </div>

        {/* Results */}
        <div>
          {result && calculated ? (
            <div className="flex flex-col gap-2 h-full">
              <div className={`rounded-xl p-5 text-center mb-1 ${
                result.netProfit >= 0
                  ? isDark ? "bg-emerald-500/8 border border-emerald-500/15" : "bg-emerald-50 border border-emerald-200"
                  : isDark ? "bg-red-500/8 border border-red-500/15" : "bg-red-50 border border-red-200"
              }`}>
                <p className={`font-inter text-xs uppercase tracking-widest mb-1 ${result.netProfit >= 0 ? isDark ? "text-emerald-500" : "text-emerald-600" : isDark ? "text-red-400" : "text-red-600"}`}>
                  Net Profit
                </p>
                <p className={`font-syne font-bold text-4xl ${result.netProfit >= 0 ? isDark ? "text-emerald-400" : "text-emerald-600" : isDark ? "text-red-400" : "text-red-500"}`}>
                  {result.netProfit >= 0 ? "+" : ""}${result.netProfit.toFixed(2)}
                </p>
                <div className="flex items-center justify-center gap-4 mt-2">
                  <span className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>
                    ROI: <strong className={isDark ? "text-white/70" : "text-[#333]"}>{result.roi.toFixed(1)}%</strong>
                  </span>
                  <span className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>
                    Margin: <strong className={isDark ? "text-white/70" : "text-[#333]"}>{result.margin.toFixed(1)}%</strong>
                  </span>
                </div>
              </div>

              {[
                { label: "Sell Price", value: `$${parseFloat(sellPrice).toFixed(2)}`, pos: true },
                { label: "Buy Price", value: `-$${parseFloat(buyPrice).toFixed(2)}`, pos: false },
                ...(parseFloat(shippingCost) > 0 ? [{ label: "Your Shipping", value: `-$${parseFloat(shippingCost).toFixed(2)}`, pos: false }] : []),
                { label: isOther ? `Custom Fee (${parseFloat(customFee) || 0}%)` : `${platform.platform} Sale Fee (${platform.saleFee}%)`, value: `-$${result.saleFeeAmt.toFixed(2)}`, pos: false },
                ...(!isOther && result.paymentFeeAmt > 0 ? [{ label: `Payment Fee (${platform.paymentFee}%)`, value: `-$${result.paymentFeeAmt.toFixed(2)}`, pos: false }] : []),
                ...(!isOther && result.platformShipping > 0 ? [{ label: "Platform Shipping", value: `-$${result.platformShipping.toFixed(2)}`, pos: false }] : []),
              ].map((row) => (
                <div key={row.label} className={`flex items-center justify-between px-4 py-2.5 rounded-xl ${isDark ? "bg-white/3" : "bg-black/3"}`}>
                  <span className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#888]"}`}>{row.label}</span>
                  <span className={`font-inter text-xs font-medium ${row.pos ? isDark ? "text-white/80" : "text-[#333]" : isDark ? "text-[#666]" : "text-[#666]"}`}>{row.value}</span>
                </div>
              ))}

              {/* Save / Reset row */}
              <div className="flex gap-2 mt-1">
                {saved ? (
                  <div className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full font-inter text-sm font-medium ${isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-600"}`}>
                    <i className="ri-checkbox-circle-line text-sm" />
                    Saved to History!
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleSave}
                    className={`flex-1 font-inter text-sm font-medium py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                      isDark
                        ? "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"
                        : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    }`}
                  >
                    <i className="ri-save-line text-sm" />
                    Save to History
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleReset}
                  className={`px-4 py-2.5 rounded-full font-inter text-sm cursor-pointer whitespace-nowrap transition-colors ${
                    isDark ? "text-[#444] hover:text-[#888]" : "text-[#ccc] hover:text-[#888]"
                  }`}
                >
                  Reset
                </button>
              </div>
            </div>
          ) : (
            <div className={`rounded-xl border-2 border-dashed h-full min-h-48 flex flex-col items-center justify-center gap-2 ${isDark ? "border-white/8" : "border-black/8"}`}>
              <i className={`ri-calculator-line text-2xl ${isDark ? "text-white/15" : "text-black/15"}`} />
              <p className={`font-inter text-sm ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>
                Enter values and hit calculate
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
